import { ReactElement, ReactNode } from "react";

import { Card, Group, rem } from "@mantine/core";

import isEmpty from "lodash/isEmpty";

import { TextWrapper } from "@/components/TextWrapper";

import { ClientHealthSummaryTypeEnum } from "./enums";

interface EmptyBadgesTextProps {
  summaryType?: ClientHealthSummaryTypeEnum;
  noInfo?: boolean;
  noneReported: boolean;
  hasChildren: boolean;
}

interface ClientBadgesWrapperProps {
  children: ReactNode;
  title: string;
  icon?: ReactElement;
  color?: string;
  iconColor?: string;
  noInfo?: boolean;
  noneReported?: boolean;
  summaryType?: ClientHealthSummaryTypeEnum;
  onClick?: () => void;
}

function EmptyBadgesText({ summaryType, noInfo, noneReported, hasChildren }: EmptyBadgesTextProps) {
  if (!hasChildren && !noInfo) {
    return (
      <TextWrapper size="sm" ml="auto" c="gray.9" ta="right">
        Select to display {summaryType ?? ""}
      </TextWrapper>
    );
  }

  if (noneReported) {
    return (
      <TextWrapper ml="auto" c="dark.9" ta="right" tt="uppercase" variant="bold">
        None reported
      </TextWrapper>
    );
  }

  if (noInfo) {
    return (
      <TextWrapper ml="auto" c="gray.6" ta="right" tt="uppercase" variant="bold">
        No info
      </TextWrapper>
    );
  }

  return null;
}

export function ClientBadgesWrapper({
  children,
  title,
  icon,
  color = "dark.9",
  noInfo = false,
  noneReported = false,
  summaryType,
  onClick,
}: ClientBadgesWrapperProps) {
  const hasChildren = !isEmpty(children);
  return (
    <Card c={color} bg="white" onClick={onClick} mb={{ base: "-xs", xs: 0 }}>
      <Group justify="space-between">
        <Group gap={rem(4)}>
          <TextWrapper fz="md" variant="bold">
            {title}
          </TextWrapper>
          {icon}
        </Group>
        <EmptyBadgesText
          noInfo={noInfo}
          summaryType={summaryType}
          hasChildren={hasChildren}
          noneReported={noneReported}
        />
      </Group>
      {hasChildren && (
        <Group gap={5} mt="xs" justify="flex-start">
          {children}
        </Group>
      )}
    </Card>
  );
}
